import React, { useState, useEffect, useRef, useCallback } from 'react'
import { cn } from '../../lib/utils'
import type { PresenterTimerState } from '../../types'

export interface PresenterTimerProps {
  isRunning: boolean
  onTimeUpdate?: (seconds: number) => void
  className?: string
}

/**
 * Formats seconds to MM:SS display
 */
function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

/**
 * PresenterTimer Component
 *
 * Displays elapsed time for the presentation.
 * Handles start/stop/pause functionality.
 */
export const PresenterTimer: React.FC<PresenterTimerProps> = ({
  isRunning,
  onTimeUpdate,
  className,
}) => {
  const [state, setState] = useState<PresenterTimerState>({
    elapsed: 0,
    isRunning: false,
  })

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number>(0)

  /**
   * Start the timer
   */
  const start = useCallback(() => {
    if (intervalRef.current) return

    startTimeRef.current = Date.now() - state.elapsed * 1000
    intervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
      setState(prev => {
        const newState = { ...prev, elapsed }
        onTimeUpdate?.(elapsed)
        return newState
      })
    }, 100) // Update every 100ms for smooth display

    setState(prev => ({ ...prev, isRunning: true }))
  }, [state.elapsed, onTimeUpdate])

  /**
   * Stop the timer
   */
  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setState(prev => ({ ...prev, isRunning: false }))
  }, [])

  /**
   * Reset the timer
   */
  const reset = useCallback(() => {
    stop()
    setState({ elapsed: 0, isRunning: false })
    onTimeUpdate?.(0)
  }, [stop, onTimeUpdate])

  /**
   * Toggle timer state
   */
  const toggle = useCallback(() => {
    if (state.isRunning) {
      stop()
    } else {
      start()
    }
  }, [state.isRunning, start, stop])

  /**
   * Handle external isRunning prop changes
   */
  useEffect(() => {
    if (isRunning && !state.isRunning) {
      start()
    } else if (!isRunning && state.isRunning) {
      stop()
    }
  }, [isRunning, state.isRunning, start, stop])

  /**
   * Cleanup interval on unmount
   */
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span
        className={cn(
          'font-mono text-lg font-medium',
          'tabular-nums tracking-tight',
          'text-foreground'
        )}
      >
        {formatTime(state.elapsed)}
      </span>
      <button
        onClick={toggle}
        className={cn(
          'p-1 rounded transition-colors',
          'hover:bg-muted',
          'text-muted-foreground',
          state.isRunning && 'text-green-600 dark:text-green-400'
        )}
        aria-label={state.isRunning ? 'Pause timer' : 'Start timer'}
        title={state.isRunning ? 'Pause (R to reset)' : 'Start'}
      >
        {state.isRunning ? (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
          </svg>
        )}
      </button>
      {state.elapsed > 0 && (
        <button
          onClick={reset}
          className="p-1 rounded transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
          aria-label="Reset timer"
          title="Reset timer"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      )}
    </div>
  )
}

export default PresenterTimer
